import { useTranslate } from "@similarweb/i18n-provider";

import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";

import { SignalFilterCategoryPanel } from "../../components/SignalFilterCategoryPanel/SignalFilterCategoryPanel";
import { ConfirmStep } from "../../components/UploadDealSignalsModal/ConfirmStep";
import type { FilterPanelDescriptor } from "../../components/UploadDealSignalsModal/types";
import { UploadDealSignalsModal } from "../../components/UploadDealSignalsModal/UploadDealSignalsModal";
import { UploadStep } from "../../components/UploadDealSignalsModal/UploadStep";
import { resolveModalStep, toPanelState } from "../../components/UploadDealSignalsModal/utils";
import { useDealSignalFileSelection } from "../../hooks/useDealSignalFileSelection";
import { useDealSignalTaxonomyFilters } from "../../hooks/useDealSignalTaxonomyFilters";
import signalRecommenderTrackingService from "../../services/tracking";
import type { DealSignalUploadInput } from "../../types/model";
import signalRecommenderI18nKeys from "../../utils/i18n";

type UploadDealSignalsModalContainerProps = {
    readonly isOpen: boolean;
    readonly onClose: VoidFunction;
    readonly onSubmit: (input: DealSignalUploadInput) => void;
};

const UploadDealSignalsModalContainer = ({ isOpen, onClose, onSubmit }: UploadDealSignalsModalContainerProps) => {
    const { translate } = useTranslate();

    const { file, validation, isValidating, selectFile } = useDealSignalFileSelection();
    const { isIntentFilterVisible, intent, technology, buildPayload } = useDealSignalTaxonomyFilters();

    const step = resolveModalStep(file, validation, isValidating);

    const handleAccept = () => {
        if (!file) {
            return;
        }

        onSubmit({ file, signalFilters: buildPayload() });
    };

    const handleRequestClose = () => {
        signalRecommenderTrackingService.trackCloseUploadModal();
        onClose();
    };

    const intentTitle = translate(signalRecommenderI18nKeys.confirm.filters.intent.label);
    const technologyTitle = translate(signalRecommenderI18nKeys.confirm.filters.technology.label);

    const filterPanels: readonly FilterPanelDescriptor[] = [
        ...(isIntentFilterVisible
            ? [
                  {
                      key: "intent",
                      title: intentTitle,
                      dataAutomation: "signal-recommender-confirm-intent-filter",
                      state: toPanelState(intent.isPending, intent.isError, intent.refetch, () =>
                          intent.viewModel ? (
                              <SignalFilterCategoryPanel
                                  dataAutomation="signal-recommender-confirm-intent-filter"
                                  title={intentTitle}
                                  viewModel={intent.viewModel}
                                  selectedIds={intent.selectedIds}
                                  onChange={intent.onChange}
                              />
                          ) : null,
                      ),
                  },
              ]
            : []),
        {
            key: "technology",
            title: technologyTitle,
            dataAutomation: "signal-recommender-confirm-technology-filter",
            state: toPanelState(technology.isPending, technology.isError, technology.refetch, () =>
                technology.viewModel ? (
                    <SignalFilterCategoryPanel
                        dataAutomation="signal-recommender-confirm-technology-filter"
                        title={technologyTitle}
                        viewModel={technology.viewModel}
                        selectedIds={technology.selectedIds}
                        onChange={technology.onChange}
                    />
                ) : null,
            ),
        },
    ];

    return (
        <UploadDealSignalsModal
            isOpen={isOpen}
            stepKind={step.kind}
            onRequestClose={handleRequestClose}
        >
            {step.kind === "upload" ? (
                <UploadStep
                    isValidating={step.isValidating}
                    validationError={step.validationError}
                    onFileSelect={selectFile}
                />
            ) : (
                <ConfirmStep
                    fileName={step.fileName}
                    companiesCount={step.companiesCount}
                    signalTypes={signalsService.availableSignalTypes}
                    filters={filterPanels}
                    onCancel={handleRequestClose}
                    onAccept={handleAccept}
                />
            )}
        </UploadDealSignalsModal>
    );
};

export default UploadDealSignalsModalContainer;
